import apisauce from 'apisauce'
import Config from '../../Config'
import type {
  CommunicationPreferenceRequest,
  CommunicationPreferenceResponse,
  PushCommunicationPreferenceRequest,
  PushCommunicationPreferenceResponse,
  SMSCommunicationPreferenceResponse,
} from '@dialogue/engage'

type LoginStore = {
  accessToken: string
}

type PushPreferences = {
  brandNewContent?: PushCommunicationPreferenceRequest['brand_new_content']
  challengeUpdates?: PushCommunicationPreferenceRequest['challenge_updates']
  habitReminders?: PushCommunicationPreferenceRequest['habit_reminders']
  importantAnnouncements?: PushCommunicationPreferenceRequest['important_announcements']
  wbiReminders?: PushCommunicationPreferenceRequest['wbi_reminders']
  intakeReminders?: PushCommunicationPreferenceRequest['intake_reminders']
}

type SMSPreferences = {
  appointmentReminders?: SMSCommunicationPreferenceResponse['appointment_reminders']
}

type ApiError = {
  errors: string[]
}

const create = (loginStore: LoginStore, baseURL = Config.ENGAGE_DOMAIN) => {
  const authToken = loginStore.accessToken
  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    timeout: 20000,
  })

  const normalizePushPreferences = (
    rawData: PushCommunicationPreferenceResponse
  ): PushPreferences => {
    return {
      brandNewContent: rawData.brand_new_content,
      challengeUpdates: rawData.challenge_updates,
      habitReminders: rawData.habit_reminders,
      importantAnnouncements: rawData.important_announcements,
      wbiReminders: rawData.wbi_reminders,
      intakeReminders: rawData.intake_reminders,
    }
  }

  const normalizeSMSPreferences = (rawData: SMSCommunicationPreferenceResponse): SMSPreferences => {
    return {
      appointmentReminders: rawData.appointment_reminders,
    }
  }

  const getSMSPreferences = (userId: string): Promise<SMSPreferences | { sms: SMSPreferences }> => {
    return api
      .get<SMSCommunicationPreferenceResponse, ApiError>(`/users/${userId}/preferences/sms`)
      .then((response) => {
        if (response.ok && response.data) {
          return {
            sms: normalizeSMSPreferences(response.data),
          }
        }
        throw response?.data && 'errors' in response.data && response.data.errors[0]
          ? response.data.errors[0]
          : {
              status: response.status,
              detail: 'Something went wrong with Engage',
            }
      })
  }

  const updatePreferences = (
    userId: string,
    preferences: CommunicationPreferenceRequest
  ): Promise<boolean> => {
    return api
      .patch<CommunicationPreferenceResponse, ApiError>(`/users/${userId}/preferences`, preferences)
      .then((response) => {
        if (response.ok) {
          return response.ok
        }
        throw (
          response?.data?.errors?.[0] || {
            status: response.status,
            detail: 'Something went wrong with Engage',
          }
        )
      })
  }

  const getPushPreferences = (userId: string): Promise<{ push: PushPreferences }> => {
    return api
      .get<PushCommunicationPreferenceResponse, ApiError>(`/users/${userId}/preferences/push`)
      .then((response) => {
        if (response.ok && response.data) {
          return {
            push: normalizePushPreferences(response.data),
          }
        }
        throw response?.data && 'errors' in response.data && response.data.errors[0]
          ? response.data.errors[0]
          : {
              status: response.status,
              detail: 'Something went wrong with Engage',
            }
      })
  }

  return {
    updatePreferences,
    getSMSPreferences,
    normalizeSMSPreferences,
    getPushPreferences,
    normalizePushPreferences,
  }
}

export default {
  create,
}
